import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Container,
  Input,
  Label,
  Alert,
  Button,
  Collapse,
} from "reactstrap";
import { useFormik } from "formik";
import toastr from "toastr";
import withRouter from "../../../components/Common/withRouter";
import { useUserMe, useUpdateClientUser } from "../../../api/users";
import { mediaUrl } from "../../../helpers/mediaHelper";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import {
  buildClientProfileSectionFormData,
  hydrateClientProfileFromMe,
} from "../clientProfileFormHelpers";
import {
  getClientProfileSectionSchema,
  touchClientProfileSection,
  applySectionValidationErrors,
  clearSectionValidationErrors,
} from "../clientProfileValidation";
import {
  CompanyInformationSection,
  FinanceBillingSection,
  InsuranceSection,
  OwnershipControlSection,
  SectionChevron,
  SectionSaveFooter,
  FieldError,
} from "../ClientProfileFormSections";
import ClientProfileSkeleton from "../ClientProfileSkeleton";

const emptyDirector = () => ({
  fullName: "",
  idFile: null,
  proofOfAddressFile: null,
  existingIdUrl: "",
  existingProofUrl: "",
});

const emptyOwner = () => ({
  fullName: "",
  percentageOfOwnership: "",
  idFile: null,
  proofOfAddressFile: null,
  existingIdUrl: "",
  existingProofUrl: "",
});

const ClientUpdate = () => {
  document.title = "Client Update | JobArk";

  const userMe = useUserMe();
  const updateUser = useUpdateClientUser();

  const [openPrimary, setOpenPrimary] = useState(true);
  const [openCompany, setOpenCompany] = useState(false);
  const [openFinance, setOpenFinance] = useState(false);
  const [openHealth, setOpenHealth] = useState(false);
  const [openOCD, setOpenOCD] = useState(false);
  const [openInsurance, setOpenInsurance] = useState(true);

  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoRemoved, setPhotoRemoved] = useState(false);
  const [savingSection, setSavingSection] = useState(null);
  const [lastValidatedSection, setLastValidatedSection] = useState(null);

  const rawUser = useMemo(() => {
    const raw = userMe?.data?.data ?? userMe?.data ?? {};
    return raw && typeof raw === "object" ? raw : {};
  }, [userMe.data]);

  const initialValues = useMemo(
    () => hydrateClientProfileFromMe(rawUser),
    [rawUser]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validateOnChange: false,
    validateOnBlur: false,
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    setErrors,
  } = formik;

  useEffect(() => {
    if (!rawUser?.photo || photoFile || photoRemoved) {
      if (photoFile) return;
      if (photoRemoved) {
        setPhotoPreview(null);
        return;
      }
      return;
    }
    setPhotoPreview(mediaUrl(rawUser.photo));
  }, [rawUser?.photo, photoFile, photoRemoved]);

  useEffect(() => {
    if (!photoFile) return;
    const reader = new FileReader();
    reader.onloadend = () => setPhotoPreview(reader.result);
    reader.readAsDataURL(photoFile);
  }, [photoFile]);

  const isProfileLoading = userMe.isPending;

  const invalid = (path) => {
    const err = getAt(errors, path);
    return typeof err === "string" && err.length > 0;
  };

  const copyPrimaryToFinance = () => {
    setFieldValue(
      "financeBilling.fullName",
      values.primaryInformation.fullName
    );
    setFieldValue("financeBilling.email", values.primaryInformation.email);
    setFieldValue(
      "financeBilling.phone",
      values.primaryInformation.phoneNumber
    );
    ["fullName", "email", "phone"].forEach((k) =>
      setFieldTouched(`financeBilling.${k}`, true, false)
    );
  };

  const addDirector = () => {
    setFieldValue("ownershipControlDeclaration.directors", [
      ...(values.ownershipControlDeclaration.directors || []),
      emptyDirector(),
    ]);
  };

  const removeDirector = (idx) => {
    const arr = [...(values.ownershipControlDeclaration.directors || [])];
    arr.splice(idx, 1);
    setFieldValue("ownershipControlDeclaration.directors", arr);
  };

  const addIndividualOwner = () => {
    setFieldValue("ownershipControlDeclaration.individualOwners", [
      ...(values.ownershipControlDeclaration.individualOwners || []),
      emptyOwner(),
    ]);
  };

  const removeIndividualOwner = (idx) => {
    const arr = [
      ...(values.ownershipControlDeclaration.individualOwners || []),
    ];
    arr.splice(idx, 1);
    setFieldValue("ownershipControlDeclaration.individualOwners", arr);
  };

  const addCorporateOwner = () => {
    setFieldValue("ownershipControlDeclaration.corporateUltimateOwners", [
      ...(values.ownershipControlDeclaration.corporateUltimateOwners || []),
      emptyOwner(),
    ]);
  };

  const removeCorporateOwner = (idx) => {
    const arr = [
      ...(values.ownershipControlDeclaration.corporateUltimateOwners || []),
    ];
    arr.splice(idx, 1);
    setFieldValue("ownershipControlDeclaration.corporateUltimateOwners", arr);
  };

  const handleOwnershipTypeChange = (type) => {
    setFieldValue("ownershipControlDeclaration.ownershipCompanyType", type);
    if (type === "individuals") {
      setFieldValue("ownershipControlDeclaration.corporateUltimateOwners", []);
      const owners = values.ownershipControlDeclaration.individualOwners || [];
      if (!owners.length) {
        setFieldValue("ownershipControlDeclaration.individualOwners", [
          emptyOwner(),
        ]);
      }
    } else if (type === "corporate") {
      setFieldValue("ownershipControlDeclaration.individualOwners", []);
      const owners =
        values.ownershipControlDeclaration.corporateUltimateOwners || [];
      if (!owners.length) {
        setFieldValue("ownershipControlDeclaration.corporateUltimateOwners", [
          emptyOwner(),
        ]);
      }
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0] || null;
    if (!file) return;
    const allowed = ["image/png", "image/jpeg", "image/jpg"];
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!allowed.includes(file.type) && !["png", "jpeg", "jpg"].includes(ext)) {
      toastr.error("Please upload a PNG, JPEG, or JPG image");
      return;
    }
    setPhotoFile(file);
    setPhotoRemoved(false);
  };

  const onRemovePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    setPhotoRemoved(true);
  };

  const openSectionPanel = (section) => {
    if (section === "primaryInformation") setOpenPrimary(true);
    if (section === "companyInformation") setOpenCompany(true);
    if (section === "financeBilling") setOpenFinance(true);
    if (section === "healthAndSafetyContact") setOpenHealth(true);
    if (section === "ownershipControlDeclaration") setOpenOCD(true);
    if (section === "insurance") setOpenInsurance(true);
  };

  const handleSectionSave = useCallback(
    async (section) => {
      const schema = getClientProfileSectionSchema(section);
      if (!schema) return;

      touchClientProfileSection(section, values, setFieldTouched);

      try {
        await schema.validate(values[section], { abortEarly: false });
        clearSectionValidationErrors(section, errors, setErrors);
        setLastValidatedSection(null);
      } catch (err) {
        applySectionValidationErrors(
          err,
          section,
          setErrors,
          setFieldTouched,
          errors
        );
        setLastValidatedSection(section);
        openSectionPanel(section);
        toastr.error("Please fix validation errors before saving");
        return;
      }

      setSavingSection(section);
      try {
        const fd = buildClientProfileSectionFormData(section, values, {
          photoFile: section === "primaryInformation" ? photoFile : undefined,
          photoRemoved: section === "primaryInformation" ? photoRemoved : false,
        });
        await updateUser.mutateAsync(fd);
        toastr.success("Saved successfully");

        if (section === "primaryInformation") {
          setPhotoFile(null);
          setPhotoRemoved(false);
        }
        if (section === "insurance") {
          setFieldValue("insurance.employersLiability", null);
          setFieldValue("insurance.publicLiability", null);
        }

        userMe.refetch?.();
      } catch (e2) {
        const msg =
          e2?.response?.data?.message ||
          e2?.message ||
          "Update failed. Please try again.";
        toastr.error(msg);
      } finally {
        setSavingSection(null);
      }
    },
    [
      values,
      errors,
      setFieldTouched,
      setErrors,
      photoFile,
      photoRemoved,
      updateUser,
      userMe,
      setFieldValue,
    ]
  );

  const isSectionSaving = (section) => savingSection === section;

  useEffect(() => {
    if (!lastValidatedSection) return;
    const sectionErrors = errors[lastValidatedSection];
    if (!sectionErrors || Object.keys(sectionErrors).length === 0) return;

    const firstPath = findFirstErrorPath(sectionErrors, lastValidatedSection);
    if (!firstPath) return;

    setTimeout(() => {
      const el = document.querySelector(`[name="${firstPath}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        if (typeof el.focus === "function") el.focus();
      }
    }, 350);
  }, [lastValidatedSection, errors]);

  return (
    <div className="page-content">
      <Container fluid className="px-4">
        <Row>
          <Col xs={12}>
            {userMe.isError && (
              <Alert color="danger" className="mb-3">
                Failed to load user.
              </Alert>
            )}

            {isProfileLoading ? (
              <ClientProfileSkeleton />
            ) : (
              <>
            <div>
              <Card className="mb-3">
                <CardHeader
                  onClick={() => setOpenPrimary((v) => !v)}
                  className="d-flex justify-content-between align-items-center"
                  style={{ cursor: "pointer", backgroundColor: "#eceeee" }}
                >
                  <h6 className="mb-0 fw-bolder">Primary Information</h6>
                  <SectionChevron open={openPrimary} />
                </CardHeader>
                <Collapse isOpen={openPrimary}>
                  <CardBody style={{ backgroundColor: "#eceeee" }}>
                    <Row className="gy-3 gx-4">
                      <Col
                        md={12}
                        className="d-flex justify-content-center mb-2"
                      >
                        <div
                          className="position-relative"
                          style={{ width: "150px", height: "150px" }}
                        >
                          <div
                            className="rounded-circle border overflow-hidden d-flex align-items-center justify-content-center bg-light"
                            style={{
                              width: 150,
                              height: 150,
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              document.getElementById("photoInput")?.click()
                            }
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) =>
                              e.key === "Enter" &&
                              document.getElementById("photoInput")?.click()
                            }
                          >
                            {photoPreview ? (
                              <img
                                src={photoPreview}
                                alt="Profile"
                                className="w-100 h-100"
                                style={{ objectFit: "cover" }}
                              />
                            ) : (
                              <div className="text-center text-muted">
                                <i
                                  className="mdi mdi-camera"
                                  style={{ fontSize: 48 }}
                                />
                                <div className="small mt-1">Upload photo</div>
                              </div>
                            )}
                          </div>
                          {photoPreview && (
                            <Button
                              type="button"
                              color="danger"
                              size="sm"
                              className="position-absolute top-0 end-0 rounded-circle p-0 d-flex align-items-center justify-content-center"
                              style={{ width: 30, height: 30 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                onRemovePhoto();
                              }}
                            >
                              <i className="mdi mdi-close" />
                            </Button>
                          )}
                          <input
                            id="photoInput"
                            type="file"
                            accept="image/png,image/jpeg,image/jpg"
                            className="d-none"
                            onChange={handlePhotoChange}
                          />
                        </div>
                      </Col>
                      <Col md={6} lg={3}>
                        <Label className="fw-bolder">Full name</Label>
                        <Input
                          name="primaryInformation.fullName"
                          value={values.primaryInformation.fullName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          invalid={invalid("primaryInformation.fullName")}
                        />
                        <FieldError
                          path="primaryInformation.fullName"
                          invalid={invalid}
                          getAt={getAt}
                          errors={errors}
                        />
                      </Col>
                      <Col md={6} lg={3}>
                        <Label className="fw-bolder">Email</Label>
                        <Input
                          name="primaryInformation.email"
                          type="email"
                          value={values.primaryInformation.email}
                          disabled
                          readOnly
                        />
                      </Col>
                      <Col md={6} lg={3}>
                        <Label className="fw-bolder">Phone</Label>
                        <PhoneInput
                          country="gb"
                          enableSearch
                          containerClass="w-100"
                          inputProps={{
                            name: "primaryInformation.phoneNumber",
                          }}
                          inputClass={
                            invalid("primaryInformation.phoneNumber")
                              ? "form-control is-invalid"
                              : "form-control"
                          }
                          value={values.primaryInformation.phoneNumber}
                          onBlur={() =>
                            setFieldTouched(
                              "primaryInformation.phoneNumber",
                              true
                            )
                          }
                          onChange={(v) =>
                            setFieldValue("primaryInformation.phoneNumber", v)
                          }
                        />
                        <FieldError
                          path="primaryInformation.phoneNumber"
                          invalid={invalid}
                          getAt={getAt}
                          errors={errors}
                        />
                      </Col>
                      <Col md={6} lg={3}>
                        <Label className="fw-bolder">Position</Label>
                        <Input
                          name="primaryInformation.position"
                          value={values.primaryInformation.position}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          invalid={invalid("primaryInformation.position")}
                        />
                        <FieldError
                          path="primaryInformation.position"
                          invalid={invalid}
                          getAt={getAt}
                          errors={errors}
                        />
                      </Col>
                    </Row>
                    <SectionSaveFooter
                      onSave={() => handleSectionSave("primaryInformation")}
                      isSaving={isSectionSaving("primaryInformation")}
                    />
                  </CardBody>
                </Collapse>
              </Card>

              <CompanyInformationSection
                open={openCompany}
                onToggle={() => setOpenCompany((v) => !v)}
                values={values}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                handleBlur={handleBlur}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                invalid={invalid}
                getAt={getAt}
                onSave={() => handleSectionSave("companyInformation")}
                isSaving={isSectionSaving("companyInformation")}
              />

              <FinanceBillingSection
                open={openFinance}
                onToggle={() => setOpenFinance((v) => !v)}
                values={values}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                handleBlur={handleBlur}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                invalid={invalid}
                getAt={getAt}
                onCopyPrimary={copyPrimaryToFinance}
                onSave={() => handleSectionSave("financeBilling")}
                isSaving={isSectionSaving("financeBilling")}
              />

              <Card className="mb-3">
                <CardHeader
                  onClick={() => setOpenHealth((v) => !v)}
                  className="d-flex justify-content-between align-items-center"
                  style={{ cursor: "pointer", backgroundColor: "#eceeee" }}
                >
                  <h6 className="mb-0 fw-bolder">
                    Health &amp; Safety Contact
                  </h6>
                  <SectionChevron open={openHealth} />
                </CardHeader>
                <Collapse isOpen={openHealth}>
                  <CardBody style={{ backgroundColor: "#eceeee" }}>
                    <Row className="gy-3 gx-4">
                      <Col md={6}>
                        <Label className="fw-bolder">Full name</Label>
                        <Input
                          name="healthAndSafetyContact.fullName"
                          value={values.healthAndSafetyContact.fullName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          invalid={invalid("healthAndSafetyContact.fullName")}
                        />
                        <FieldError
                          path="healthAndSafetyContact.fullName"
                          invalid={invalid}
                          getAt={getAt}
                          errors={errors}
                        />
                      </Col>
                      <Col md={6}>
                        <Label className="fw-bolder">Email</Label>
                        <Input
                          type="email"
                          name="healthAndSafetyContact.email"
                          value={values.healthAndSafetyContact.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          invalid={invalid("healthAndSafetyContact.email")}
                        />
                        <FieldError
                          path="healthAndSafetyContact.email"
                          invalid={invalid}
                          getAt={getAt}
                          errors={errors}
                        />
                      </Col>
                      <Col md={6}>
                        <Label className="fw-bolder">Phone</Label>
                        <PhoneInput
                          country="gb"
                          enableSearch
                          containerClass="w-100"
                          inputProps={{
                            name: "healthAndSafetyContact.phoneNumber",
                          }}
                          inputClass={
                            invalid("healthAndSafetyContact.phoneNumber")
                              ? "form-control is-invalid"
                              : "form-control"
                          }
                          value={values.healthAndSafetyContact.phoneNumber}
                          onBlur={() =>
                            setFieldTouched(
                              "healthAndSafetyContact.phoneNumber",
                              true
                            )
                          }
                          onChange={(v) =>
                            setFieldValue(
                              "healthAndSafetyContact.phoneNumber",
                              v
                            )
                          }
                        />
                        <FieldError
                          path="healthAndSafetyContact.phoneNumber"
                          invalid={invalid}
                          getAt={getAt}
                          errors={errors}
                        />
                      </Col>
                    </Row>
                    <SectionSaveFooter
                      onSave={() => handleSectionSave("healthAndSafetyContact")}
                      isSaving={isSectionSaving("healthAndSafetyContact")}
                    />
                  </CardBody>
                </Collapse>
              </Card>

              <OwnershipControlSection
                open={openOCD}
                onToggle={() => setOpenOCD((v) => !v)}
                values={values}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                handleBlur={handleBlur}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                invalid={invalid}
                getAt={getAt}
                addDirector={addDirector}
                removeDirector={removeDirector}
                addIndividualOwner={addIndividualOwner}
                removeIndividualOwner={removeIndividualOwner}
                addCorporateOwner={addCorporateOwner}
                removeCorporateOwner={removeCorporateOwner}
                onOwnershipTypeChange={handleOwnershipTypeChange}
                onSave={() => handleSectionSave("ownershipControlDeclaration")}
                isSaving={isSectionSaving("ownershipControlDeclaration")}
              />

              <InsuranceSection
                open={openInsurance}
                onToggle={() => setOpenInsurance((v) => !v)}
                values={values}
                errors={errors}
                touched={touched}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                invalid={invalid}
                getAt={getAt}
                onSave={() => handleSectionSave("insurance")}
                isSaving={isSectionSaving("insurance")}
              />
            </div>
              </>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

function getAt(obj, path) {
  return pathToArr(path).reduce(
    (o, k) =>
      o && Object.prototype.hasOwnProperty.call(o, k) ? o[k] : undefined,
    obj
  );
}

function pathToArr(p) {
  return p
    .replace(/\[(\d+)\]/g, ".$1")
    .split(".")
    .filter(Boolean);
}

function findFirstErrorPath(err, prefix = "") {
  if (!err || typeof err !== "object") return null;
  if (typeof err === "string") return prefix.replace(/^\./, "");
  for (const key of Object.keys(err)) {
    const next = prefix ? `${prefix}.${key}` : key;
    const val = err[key];
    if (typeof val === "string") return next;
    const nested = findFirstErrorPath(val, next);
    if (nested) return nested;
  }
  return null;
}

export default withRouter(ClientUpdate);
